import React from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useEdgesState,
  useNodesState,
  addEdge,
  reconnectEdge,
  MarkerType,
  type Edge,
  type Node,
  type Connection,
  ReactFlowProvider,
  useReactFlow,
  getConnectedEdges
} from '@xyflow/react';
import { ActionIcon, Alert, Button, Group, Text, Tooltip } from '@mantine/core';
import {
  IconSun,
  IconMoon,
  IconLayoutDistributeHorizontal,
  IconDeviceFloppy,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconPlus,
  IconTag,
  IconAlertCircle,
  IconTargetArrow
} from '@tabler/icons-react';
import '@xyflow/react/dist/style.css';

import { parseDsl } from '../../dsl/parse';
import { generateDsl } from '../../dsl/generate';
import type {
  ParsedNode,
  ParsedEdge,
  DeclaredInput,
  NodeRegistry,
  NodeConfig,
  HandleDefinition,
  EditorSnapshot
} from '../../types';
import type { CodeBlockOutput, ExecutionError, ExecutionTrace } from '../../types/editor';
import type { TraceLabels } from '../../trace/traceShapes';
import { DEFAULT_TRACE_LABELS } from '../../trace/traceShapes';
import { NodeOutputsPanel } from '../../trace/NodeOutputsPanel';
import { resolveCodeBlockInputTypes, resolveCodeBlockOutputTypes } from '../../utils/resolveCodeBlockTypes';
import { useHistory } from '../../state/history';
import { layout, type NodeLayout } from '../../layout/elk';
import { RuleNode } from '../RuleNode';
import type { RuleNodeData } from '../RuleNode';
import { TypedConnectionLine } from '../TypedConnectionLine';
import { TypedEdge } from '../TypedEdge';
import { EdgeTooltip } from '../EdgeTooltip';
import { NodePalette } from '../NodePalette';
import { NodeConfigDrawer } from '../NodeConfigDrawer';
import '../../styles/xy-theme.css';
import classes from './RuleEditor.module.css';

const nodeTypes = { rule: RuleNode } as const;
const edgeTypes = { typed: TypedEdge } as const;

export type ThemeMode = 'light' | 'dark';

export type RuleEditorProps = {
  /** YAML string to load */
  dsl: string;
  /** Node definitions with types */
  nodeRegistry: NodeRegistry;
  /** Called when user saves */
  onSave?: ( yaml: string ) => void;
  /** Called when state diverges from loaded YAML */
  onChange?: ( dirty: boolean ) => void;
  /** Color scheme */
  theme?: ThemeMode;
  /** Disable editing */
  readOnly?: boolean;
  /** Auto-layout or preserve positions */
  initialLayout?: 'auto' | 'preserved';
  /** Execution trace data to overlay on the graph */
  executionTrace?: ExecutionTrace;
  /**
   * Top-level execution error information. When set, a red banner is shown
   * above the diagram. If `nodeId` is present the banner also shows a
   * "Jump to failed node" action that centres the canvas and briefly
   * flashes the node.
   */
  executionError?: ExecutionError;
  /** Translated UI labels for the trace viewer (TraceValueView, NodeOutputsPanel). */
  traceLabels?: TraceLabels;
  /** When true, edge value labels are visible on initial render (requires `executionTrace`). Defaults to false. */
  showEdgeLabelsByDefault?: boolean;
};

/**
 * Build ReactFlow edge style based on theme.
 */
function edgeStyle( themeMode: ThemeMode ) {
  return {
    stroke: themeMode === 'dark' ? '#64748b' : '#475569',
    strokeWidth: 2
  };
}

/**
 * Build minimap node color from node data.
 */
function minimapNodeColor( node: Node ): string {
  const colors: Record<string, string> = {
    Input: '#10b981',
    Output: '#ef4444',
    Logic: '#60a5fa',
    Property: '#a78bfa',
    Template: '#fb7185',
    CodeBlock: '#fbbf24',
    ForEach: '#34d399',
    SendUserNotification: '#f472b6',
    StockManagementWarehouses: '#38bdf8',
    ItemsPublishingInventory: '#818cf8'
  };
  return colors[( node.data as RuleNodeData )?.type] || '#6b7280';
}

/**
 * Convert parsed DSL nodes into ReactFlow nodes using layout positions.
 * Enriches node data with edge-inferred handles so that dynamic ports
 * (not in the registry) are rendered and edges can attach to them.
 */
function toRfNodes(
  parsedNodes: ParsedNode[],
  parsedEdges: ParsedEdge[],
  positions: Map<string, NodeLayout>,
  registry?: NodeRegistry,
  executionTrace?: ExecutionTrace,
  traceLabels?: TraceLabels
): Node[] {
  // Collect edge-referenced handles per node
  const edgeInputsByNode = new Map<string, Set<string>>();
  const edgeOutputsByNode = new Map<string, Set<string>>();
  for ( const e of parsedEdges ) {
    if ( e.sourceHandle ) {
      if ( !edgeOutputsByNode.has( e.source )) edgeOutputsByNode.set( e.source, new Set());
      edgeOutputsByNode.get( e.source )!.add( e.sourceHandle );
    }
    if ( e.targetHandle ) {
      if ( !edgeInputsByNode.has( e.target )) edgeInputsByNode.set( e.target, new Set());
      edgeInputsByNode.get( e.target )!.add( e.targetHandle );
    }
  }

  return parsedNodes.map(( n ) => {
    const nodeLayout = positions.get( n.id ) ?? { x: 0, y: 0, width: 230, height: 80 };

    // Merge registry handles with edge-inferred dynamic handles for rendering
    const inputNames = new Set( n.inputs.map(( h ) => h.name ));
    const allInputs = [...n.inputs];
    for ( const name of edgeInputsByNode.get( n.id ) ?? []) {
      if ( !inputNames.has( name )) {
        allInputs.push({ name, type: 'any' });
      }
    }

    // For CodeBlock nodes, use configured outputs from values if available
    const isCodeBlock = registry?.[n.type]?.name === 'CodeBlock';
    const configuredOutputs = isCodeBlock
      ? ( n.values?.outputs as CodeBlockOutput[] | undefined )
      : undefined;

    let allOutputs: HandleDefinition[];
    if ( configuredOutputs && configuredOutputs.length > 0 ) {
      allOutputs = configuredOutputs.map(( o ) => ({ name: o.name, type: o.type }));
    } else {
      allOutputs = [...n.outputs];
    }

    // Add any edge-referenced outputs not already present
    const outputNames = new Set( allOutputs.map(( h ) => h.name ));
    for ( const name of edgeOutputsByNode.get( n.id ) ?? []) {
      if ( !outputNames.has( name )) {
        allOutputs.push({ name, type: 'any' });
      }
    }

    return {
      id: n.id,
      type: 'rule',
      position: { x: nodeLayout.x, y: nodeLayout.y },
      style: {
        width: nodeLayout.width
      },
      data: {
        id: n.id,
        type: n.type,
        inputs: allInputs,
        outputs: allOutputs,
        values: n.values,
        // Carried, never rendered: a wrapper's nested `source` is not a graph member, and a
        // save that dropped it would mint a wrapper with nothing to drain.
        ...( n.source ? { source: n.source } : {}),
        ...( executionTrace?.nodeTraces[n.id] ? { traceEntry: executionTrace.nodeTraces[n.id] } : {}),
        ...( traceLabels ? { traceLabels } : {})
      }
    };
  });
}

/**
 * Convert parsed DSL edges into ReactFlow edges.
 */
function toRfEdges(
  parsedEdges: ParsedEdge[],
  themeMode: ThemeMode,
  executionTrace?: ExecutionTrace,
  showEdgeLabels?: boolean,
  traceLabels?: TraceLabels
): Edge[] {
  return parsedEdges.map(( e ) => {
    return {
      id: e.id,
      type: 'typed',
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      markerEnd: { type: MarkerType.ArrowClosed },
      deletable: true,
      reconnectable: true,
      style: edgeStyle( themeMode ),
      data: {
        optional: e.optional,
        // Pass the full `nodeTraces` map; `TypedEdge` derives the value for this
        // edge at render time from the source node's output using graph topology.
        // Replaces the previous top-level `edgeValues` lookup.
        ...( executionTrace ? { nodeTraces: executionTrace.nodeTraces, showLabel: showEdgeLabels } : {}),
        ...( traceLabels ? { traceLabels } : {})
      }
    };
  });
}

/**
 * Extract node configs (values + inputsConfig) from parsed nodes.
 */
function extractNodeConfigs( parsedNodes: ParsedNode[]): Record<string, NodeConfig> {
  const configs: Record<string, NodeConfig> = {};
  for ( const n of parsedNodes ) {
    if ( n.values || n.inputsConfig ) {
      configs[n.id] = {
        ...( n.values ? { values: n.values } : {}),
        ...( n.inputsConfig ? { inputsConfig: n.inputsConfig } : {})
      };
    }
  }
  return configs;
}

/**
 * Convert current ReactFlow state back to ParsedNode[] and ParsedEdge[]
 * for DSL generation.
 */
function toGeneratorInput(
  rfNodes: Node[],
  rfEdges: Edge[],
  nodeConfigs: Record<string, NodeConfig>,
  registry: NodeRegistry
): { nodes: ParsedNode[]; edges: ParsedEdge[] } {
  const nodes: ParsedNode[] = rfNodes.map(( n ) => {
    const data = n.data as RuleNodeData;
    const config = nodeConfigs[n.id];
    const entry = registry[data.type];

    return {
      id: n.id,
      type: data.type,
      inputs: entry ? entry.inputs : data.inputs,
      outputs: entry ? entry.outputs : data.outputs,
      ...( config?.values ? { values: config.values } : data.values ? { values: data.values } : {}),
      ...( config?.inputsConfig ? { inputsConfig: config.inputsConfig } : {}),
      ...( data.source ? { source: data.source } : {})
    };
  });

  const edges: ParsedEdge[] = rfEdges.map(( e ) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle ?? undefined,
    targetHandle: e.targetHandle ?? undefined,
    ...( e.data?.optional ? { optional: true } : {})
  }));

  return { nodes, edges };
}

/**
 * Generate a unique node ID for a given type.
 * First instance: "{type}", subsequent: "{type}-1", "{type}-2", etc.
 */
function generateNodeId( nodeType: string, existingNodes: Node[]): string {
  const existingIds = new Set( existingNodes.map(( n ) => n.id ));
  if ( !existingIds.has( nodeType )) return nodeType;

  let counter = 1;
  while ( existingIds.has( `${nodeType}-${counter}` )) {
    counter++;
  }
  return `${nodeType}-${counter}`;
}

function RuleEditorInner({
  dsl,
  nodeRegistry,
  onSave,
  onChange,
  theme: themeProp,
  readOnly = false,
  initialLayout = 'auto',
  executionTrace,
  executionError,
  traceLabels,
  showEdgeLabelsByDefault = false
}: RuleEditorProps ) {
  const [ flashNodeId, setFlashNodeId ] = React.useState<string | null>( null );
  const flashTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>( null );
  const [ nodes, setNodes, onNodesChange ] = useNodesState<Node>([]);
  const [ edges, setEdges, onEdgesChange ] = useEdgesState<Edge>([]);
  const [ themeMode, setThemeMode ] = React.useState<ThemeMode>( themeProp ?? 'dark' );
  const [ showEdgeLabels, setShowEdgeLabels ] = React.useState( showEdgeLabelsByDefault );
  const [ isLoading, setIsLoading ] = React.useState( true );
  const [ nodeConfigs, setNodeConfigs ] = React.useState<Record<string, NodeConfig>>({});
  const [ declaredInputs, setDeclaredInputs ] = React.useState<DeclaredInput[]>([]);
  const [ loadedDsl, setLoadedDsl ] = React.useState( '' );
  const [ paletteOpen, setPaletteOpen ] = React.useState( false );
  const [ palettePosition, setPalettePosition ] = React.useState<{ x: number; y: number } | undefined>();
  const [ configDrawerOpen, setConfigDrawerOpen ] = React.useState( false );
  const [ selectedNodeId, setSelectedNodeId ] = React.useState<string | null>( null );

  const mousePositionRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const reconnectingRef = React.useRef( false );

  const reactFlowInstance = useReactFlow();
  const history = useHistory( 50 );

  // Helper to capture a snapshot of the current editor state
  const captureSnapshot = React.useCallback((): EditorSnapshot => ({
    nodes: JSON.parse( JSON.stringify( nodes )),
    edges: JSON.parse( JSON.stringify( edges )),
    nodeConfigs: JSON.parse( JSON.stringify( nodeConfigs )),
    declaredInputs: JSON.parse( JSON.stringify( declaredInputs ))
  }), [ nodes, edges, nodeConfigs, declaredInputs ]);

  // Push current state as a snapshot before a mutation
  const pushSnapshot = React.useCallback(() => {
    history.pushSnapshot( captureSnapshot());
  }, [ history, captureSnapshot ]);

  // Track selected edges for the EdgeTooltip
  const selectedEdges = React.useMemo(
    () => edges.filter(( e ) => e.selected ),
    [edges]
  );

  // Toggle the optional flag on an edge
  const handleToggleOptional = React.useCallback(( edgeId: string, optional: boolean ) => {
    if ( readOnly ) return;
    pushSnapshot();
    setEdges(( eds ) => eds.map(( e ) =>
      e.id === edgeId ? { ...e, data: { ...e.data, optional }} : e ));
  }, [ readOnly, setEdges, pushSnapshot ]);

  // Apply a snapshot to all state
  const applySnapshot = React.useCallback(( snapshot: EditorSnapshot ) => {
    setNodes( snapshot.nodes );
    setEdges( snapshot.edges );
    setNodeConfigs( snapshot.nodeConfigs );
    setDeclaredInputs( snapshot.declaredInputs );
  }, [ setNodes, setEdges ]);

  // Undo handler
  const handleUndo = React.useCallback(() => {
    const snapshot = history.undo( captureSnapshot());
    if ( snapshot ) applySnapshot( snapshot );
  }, [ history, captureSnapshot, applySnapshot ]);

  // Redo handler
  const handleRedo = React.useCallback(() => {
    const snapshot = history.redo( captureSnapshot());
    if ( snapshot ) applySnapshot( snapshot );
  }, [ history, captureSnapshot, applySnapshot ]);

  // Node creation from palette — place at the cursor position where palette was opened
  const handlePaletteSelect = React.useCallback(( nodeType: string ) => {
    const entry = nodeRegistry[nodeType];
    if ( !entry ) return;

    pushSnapshot();

    const newId = generateNodeId( nodeType, nodes );

    // Use palette position (cursor) if available, otherwise viewport center
    const screenPos = palettePosition ?? (() => {
      const flowContainer = document.querySelector( `.${classes.flowContainer}` );
      const w = flowContainer?.clientWidth ?? 800;
      const h = flowContainer?.clientHeight ?? 600;
      return { x: w / 2, y: h / 2 };
    })();
    const position = reactFlowInstance.screenToFlowPosition( screenPos );

    const newNode: Node = {
      id: newId,
      type: 'rule',
      position,
      style: { width: 230, height: 80 },
      data: {
        id: newId,
        type: nodeType,
        inputs: entry.inputs,
        outputs: entry.outputs
      } satisfies RuleNodeData
    };

    setNodes(( currentNodes ) => [ ...currentNodes, newNode ]);
  }, [ nodeRegistry, nodes, reactFlowInstance, pushSnapshot, setNodes, palettePosition ]);

  // Open palette at a specific position (mouse cursor)
  const openPaletteAt = React.useCallback(( x: number, y: number ) => {
    setPalettePosition({ x, y });
    setPaletteOpen( true );
  }, []);

  const closePalette = React.useCallback(() => {
    setPaletteOpen( false );
    setPalettePosition( undefined );
  }, []);

  // Track mouse position over the flow container
  const handleMouseMove = React.useCallback(( e: React.MouseEvent ) => {
    mousePositionRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  // Right-click opens palette at cursor
  const handleContextMenu = React.useCallback(( e: React.MouseEvent ) => {
    e.preventDefault();
    if ( readOnly ) return;
    openPaletteAt( e.clientX, e.clientY );
  }, [ readOnly, openPaletteAt ]);

  // Keyboard shortcuts for undo/redo and palette toggle
  React.useEffect(() => {
    const handler = ( e: KeyboardEvent ) => {
      if ( readOnly ) return;

      // Shift+A: toggle palette at mouse cursor
      if ( e.shiftKey && e.key === 'A' && !e.metaKey && !e.ctrlKey ) {
        const target = e.target as HTMLElement;
        if ( target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable ) return;

        e.preventDefault();
        if ( paletteOpen ) {
          closePalette();
        } else {
          openPaletteAt( mousePositionRef.current.x, mousePositionRef.current.y );
        }
        return;
      }

      // Ctrl/Cmd+Z: undo/redo
      const isMod = e.metaKey || e.ctrlKey;
      if ( !isMod || e.key.toLowerCase() !== 'z' ) return;

      e.preventDefault();
      if ( e.shiftKey ) {
        handleRedo();
      } else {
        handleUndo();
      }
    };
    window.addEventListener( 'keydown', handler );
    return () => window.removeEventListener( 'keydown', handler );
  }, [ readOnly, paletteOpen, handleUndo, handleRedo, openPaletteAt, closePalette ]);

  // Sync theme prop
  React.useEffect(() => {
    if ( themeProp ) {
      setThemeMode( themeProp );
    }
  }, [themeProp]);

  // Sync trace data into nodes and edges without re-parsing DSL
  React.useEffect(() => {
    if ( !executionTrace ) return;
    setNodes(( nds ) => nds.map(( n ) => ({
      ...n,
      data: {
        ...n.data,
        traceEntry: executionTrace.nodeTraces[n.id],
        traceLabels
      }
    })));
    setEdges(( eds ) => eds.map(( e ) => ({
      ...e,
      data: {
        ...e.data,
        // Pass the full `nodeTraces` map; `TypedEdge` derives the edge's
        // displayed value at render time from the source node's output.
        nodeTraces: executionTrace.nodeTraces,
        showLabel: showEdgeLabels,
        traceLabels
      }
    })));
  }, [ showEdgeLabels, executionTrace, setNodes, setEdges, traceLabels ]);

  // Parse + layout when DSL changes
  React.useEffect(() => {
    let canceled = false;
    setIsLoading( true );

    ( async () => {
      try {
        const parsed = parseDsl( dsl, nodeRegistry );
        const positions = await layout( parsed.nodes, parsed.edges );

        if ( canceled ) return;

        setNodes( toRfNodes( parsed.nodes, parsed.edges, positions, nodeRegistry, executionTrace, traceLabels ));
        setEdges( toRfEdges( parsed.edges, themeMode, executionTrace, showEdgeLabels, traceLabels ));
        setNodeConfigs( extractNodeConfigs( parsed.nodes ));
        setDeclaredInputs( parsed.declaredInputs );
        setLoadedDsl( dsl );
        setIsLoading( false );

        // Set initial history snapshot (no undo entry)
        history.setCurrent({
          nodes: toRfNodes( parsed.nodes, parsed.edges, positions, nodeRegistry, executionTrace, traceLabels ),
          edges: toRfEdges( parsed.edges, themeMode, executionTrace, showEdgeLabels, traceLabels ),
          nodeConfigs: extractNodeConfigs( parsed.nodes ),
          declaredInputs: parsed.declaredInputs
        });
      } catch ( err ) {
        if ( !canceled ) {
          console.error( 'RuleEditor: layout failed', err );
          setIsLoading( false );
        }
      }
    })();

    return () => {
      canceled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ dsl, nodeRegistry ]);

  // Track dirty state (debounced to avoid running generateDsl on every pan/zoom/select)
  React.useEffect(() => {
    if ( !loadedDsl || isLoading ) return;
    const timer = setTimeout(() => {
      const { nodes: pn, edges: pe } = toGeneratorInput( nodes, edges, nodeConfigs, nodeRegistry );
      const currentYaml = generateDsl( pn, pe, declaredInputs );
      const dirty = currentYaml !== loadedDsl;
      onChange?.( dirty );
    }, 300 );
    return () => clearTimeout( timer );
  }, [ nodes, edges, nodeConfigs, declaredInputs, loadedDsl, isLoading, onChange, nodeRegistry ]);

  // Edge creation
  const onConnect = React.useCallback(( params: unknown ) => {
    if ( readOnly ) return;
    pushSnapshot();
    const edge: Edge = {
      ...( params as Edge ),
      id: `e-${crypto.randomUUID()}`,
      type: 'typed',
      style: edgeStyle( themeMode ),
      markerEnd: { type: MarkerType.ArrowClosed },
      deletable: true,
      data: { optional: false }
    };
    setEdges(( eds ) => addEdge( edge, eds ));
  }, [ setEdges, themeMode, readOnly, pushSnapshot ]);

  // Edge deletion
  const onEdgesDelete = React.useCallback(( _edgesToDelete: Edge[]) => {
    pushSnapshot();
    // ReactFlow handles removal; nothing extra needed
  }, [pushSnapshot]);

  // Confirmation check before deletion — fires before ReactFlow removes elements
  const onBeforeDelete = React.useCallback( async ({ nodes: nodesToDelete }: { nodes: Node[]; edges: Edge[] }) => {
    if ( nodesToDelete.length === 0 ) return true;

    const connectedEdges = getConnectedEdges( nodesToDelete, edges );
    if ( connectedEdges.length >= 3 ) {
      const nodeNames = nodesToDelete.map(( n ) => ( n.data as RuleNodeData ).type ).join( ', ' );
      return window.confirm(
        `Delete ${nodesToDelete.length} node(s) (${nodeNames})? This will remove ${connectedEdges.length} connected edge(s).`
      );
    }
    return true;
  }, [edges]);

  // Node deletion — fires after ReactFlow has already removed the nodes
  const onNodesDelete = React.useCallback(( nodesToDelete: Node[]) => {
    pushSnapshot();

    // Clean up nodeConfigs for removed nodes
    const nodeIdsToRemove = new Set( nodesToDelete.map(( n ) => n.id ));
    setNodeConfigs(( prev ) => {
      const next = { ...prev };
      for ( const id of nodeIdsToRemove ) {
        delete next[id];
      }
      return next;
    });
  }, [pushSnapshot]);

  // Node drag end — snapshot for position changes
  const onNodeDragStop = React.useCallback(() => {
    pushSnapshot();
  }, [pushSnapshot]);

  // Edge reconnection — drag an edge endpoint to a new port
  const onReconnectStart = React.useCallback(() => {
    reconnectingRef.current = true;
  }, []);

  const onReconnect = React.useCallback(( oldEdge: Edge, newConnection: Connection ) => {
    reconnectingRef.current = false;
    pushSnapshot();
    setEdges(( eds ) => reconnectEdge( oldEdge, newConnection, eds ));
  }, [ pushSnapshot, setEdges ]);

  const onReconnectEnd = React.useCallback(( _event: MouseEvent | TouchEvent, edge: Edge ) => {
    if ( reconnectingRef.current ) {
      // Dropped on invalid target — remove the edge
      reconnectingRef.current = false;
      pushSnapshot();
      setEdges(( eds ) => eds.filter(( e ) => e.id !== edge.id ));
    }
  }, [ pushSnapshot, setEdges ]);

  // Node double-click — open config drawer
  const onNodeDoubleClick = React.useCallback(( _event: React.MouseEvent, node: Node ) => {
    setSelectedNodeId( node.id );
    setConfigDrawerOpen( true );
  }, []);

  // Config drawer callbacks
  const handleValuesChange = React.useCallback(( values: Record<string, unknown> ) => {
    if ( !selectedNodeId ) return;
    pushSnapshot();
    setNodeConfigs(( prev ) => ({
      ...prev,
      [selectedNodeId]: { ...prev[selectedNodeId], values }
    }));

    // Update node data outputs for CodeBlock nodes when outputs config changes
    const configuredOutputs = values.outputs as CodeBlockOutput[] | undefined;
    if ( configuredOutputs ) {
      setNodes(( currentNodes ) => currentNodes.map(( n ) => {
        if ( n.id !== selectedNodeId ) return n;
        const data = n.data as RuleNodeData;
        const entry = nodeRegistry[data.type];
        if ( entry?.name !== 'CodeBlock' ) return n;
        const newOutputs: HandleDefinition[] = configuredOutputs.length > 0
          ? configuredOutputs.map(( o ) => ({ name: o.name, type: o.type }))
          : entry.outputs;
        return { ...n, data: { ...data, outputs: newOutputs }};
      }));
    }
  }, [ selectedNodeId, pushSnapshot, setNodes, nodeRegistry ]);

  const handleInputsConfigChange = React.useCallback(( inputsConfig: Record<string, unknown> ) => {
    if ( !selectedNodeId ) return;
    pushSnapshot();
    setNodeConfigs(( prev ) => ({
      ...prev,
      [selectedNodeId]: { ...prev[selectedNodeId], inputsConfig }
    }));
  }, [ selectedNodeId, pushSnapshot ]);

  const handleDeclaredInputsChange = React.useCallback(( newDeclaredInputs: DeclaredInput[]) => {
    pushSnapshot();
    setDeclaredInputs( newDeclaredInputs );
  }, [pushSnapshot]);

  // Theme toggle
  const toggleTheme = React.useCallback(() => {
    setThemeMode(( prev ) => prev === 'dark' ? 'light' : 'dark' );
  }, []);

  // Auto-layout
  const handleAutoLayout = React.useCallback( async () => {
    try {
      const { nodes: pn, edges: pe } = toGeneratorInput( nodes, edges, nodeConfigs, nodeRegistry );
      const positions = await layout( pn, pe );
      setNodes(( currentNodes ) =>
        currentNodes.map(( n ) => {
          const pos = positions.get( n.id );
          if ( !pos ) return n;
          return {
            ...n,
            position: { x: pos.x, y: pos.y },
            style: { ...n.style, width: pos.width, height: pos.height }
          };
        }));
      // Fit view after layout — use rAF to ensure positions are rendered
      requestAnimationFrame(() => {
        reactFlowInstance.fitView({ padding: 0.7 });
      });
    } catch ( err ) {
      console.error( 'RuleEditor: auto-layout failed', err );
    }
  }, [ nodes, edges, nodeConfigs, nodeRegistry, setNodes, reactFlowInstance ]);

  // Save
  const handleSave = React.useCallback(() => {
    if ( !onSave ) return;
    const { nodes: pn, edges: pe } = toGeneratorInput( nodes, edges, nodeConfigs, nodeRegistry );
    const yaml = generateDsl( pn, pe, declaredInputs );
    onSave( yaml );
  }, [ nodes, edges, nodeConfigs, declaredInputs, nodeRegistry, onSave ]);

  // Cleanup flash timeout on unmount to avoid setting state after unmount.
  React.useEffect(() => {
    return () => {
      if ( flashTimeoutRef.current ) {
        clearTimeout( flashTimeoutRef.current );
        flashTimeoutRef.current = null;
      }
    };
  }, []);

  // Generic "jump to node" — centres the canvas on a node by id and flashes it
  // for ~1s via a className applied to the React Flow node wrapper. Used both
  // by the failure-banner "Jump to failed node" action and by the
  // NodeOutputsPanel accordion controls.
  const handleJumpToNode = React.useCallback(( nodeId: string ) => {
    if ( !nodeId ) return;

    try {
      reactFlowInstance.fitView({ nodes: [{ id: nodeId }], padding: 0.3, duration: 300 });
    } catch ( err ) {
      console.error( 'RuleEditor: fitView on node failed', err );
    }

    setFlashNodeId( nodeId );
    if ( flashTimeoutRef.current ) clearTimeout( flashTimeoutRef.current );
    flashTimeoutRef.current = setTimeout(() => {
      setFlashNodeId( null );
      flashTimeoutRef.current = null;
    }, 1000 );
  }, [reactFlowInstance]);

  // "Jump to failed node" — delegates to the generic jump helper.
  const handleJumpToFailedNode = React.useCallback(() => {
    if ( !executionError?.nodeId ) return;
    handleJumpToNode( executionError.nodeId );
  }, [ executionError, handleJumpToNode ]);

  // Apply flash className to the failed node via setNodes so the flash
  // animation runs without requiring RuleNode component changes.
  React.useEffect(() => {
    setNodes(( nds ) => nds.map(( n ) => {
      const shouldFlash = flashNodeId === n.id;
      const existingClassName = n.className ?? '';
      const hadFlash = existingClassName.includes( classes.nodeFlash );
      if ( shouldFlash === hadFlash ) return n;
      const nextClassName = shouldFlash
        ? [ existingClassName, classes.nodeFlash ].filter( Boolean ).join( ' ' )
        : existingClassName.split( ' ' ).filter(( c ) => c !== classes.nodeFlash )
.join( ' ' );
      return { ...n, className: nextClassName || undefined };
    }));
  }, [ flashNodeId, setNodes ]);

  // Build banner text (with `{{nodeId}}` placeholder support) and the jump label.
  const bannerText = React.useMemo(() => {
    if ( !executionError ) return '';
    const template = traceLabels?.executionFailedBanner
      ?? DEFAULT_TRACE_LABELS.executionFailedBanner
      ?? 'Ejecución de la regla falló en el nodo `{{nodeId}}`';
    const nodeIdDisplay = executionError.nodeId ?? '—';
    const base = template.replace( /\{\{nodeId\}\}/g, nodeIdDisplay );
    const codePart = executionError.code ? ` — ${executionError.code}` : '';
    const errorTypeLabel = executionError.errorType === 'transient'
      ? ( traceLabels?.errorTypeTransient ?? DEFAULT_TRACE_LABELS.errorTypeTransient ?? 'transient' )
      : ( traceLabels?.errorTypePermanent ?? DEFAULT_TRACE_LABELS.errorTypePermanent ?? 'permanent' );
    const typePart = ` (${errorTypeLabel})`;
    return `${base}${codePart}${typePart}`;
  }, [ executionError, traceLabels ]);

  const jumpLabel = traceLabels?.jumpToFailedNode
    ?? DEFAULT_TRACE_LABELS.jumpToFailedNode
    ?? 'Ir al nodo que falló';

  return (
    <div className={classes.container} data-theme={themeMode}>
      <div className={classes.toolbar}>
        <div className={classes.title}>Rule Flow Editor</div>
        <div className={classes.toolbarActions}>
          {!readOnly && (
            <>
              <Tooltip label="Add Node (Shift+A / Right Click)">
                <ActionIcon
                  variant="light"
                  color="teal"
                  size="lg"
                  onClick={() => {
                    if ( paletteOpen ) closePalette();
                    else setPaletteOpen( true );
                  }}
                  className={classes.toolbarButton}
                >
                  <IconPlus size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Undo (Ctrl+Z)">
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="lg"
                  onClick={handleUndo}
                  disabled={!history.canUndo}
                  className={classes.toolbarButton}
                >
                  <IconArrowBackUp size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Redo (Ctrl+Shift+Z)">
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="lg"
                  onClick={handleRedo}
                  disabled={!history.canRedo}
                  className={classes.toolbarButton}
                >
                  <IconArrowForwardUp size={18} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
          {onSave && (
            <Tooltip label="Save">
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                onClick={handleSave}
                className={classes.toolbarButton}
              >
                <IconDeviceFloppy size={18} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Auto Layout">
            <ActionIcon
              variant="light"
              color="gray"
              size="lg"
              onClick={handleAutoLayout}
              className={classes.toolbarButton}
            >
              <IconLayoutDistributeHorizontal size={18} />
            </ActionIcon>
          </Tooltip>
          {executionTrace && (
            <Tooltip label={showEdgeLabels ? 'Hide edge values' : 'Show edge values'}>
              <ActionIcon
                variant={showEdgeLabels ? 'filled' : 'light'}
                color="grape"
                size="lg"
                onClick={() => setShowEdgeLabels(( prev ) => !prev )}
                className={classes.toolbarButton}
              >
                <IconTag size={18} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={toggleTheme}
              className={classes.themeToggle}
            >
              {themeMode === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      {executionError && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={18} />}
          className={classes.banner}
          radius="sm"
        >
          <Group justify="space-between" wrap="wrap" gap="sm" align="center">
            <Text size="sm" style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
              {bannerText}
              {executionError.message ? (
                <Text component="span" size="sm" c="dimmed" ml={6}>
                  {`— ${executionError.message}`}
                </Text>
              ) : null}
            </Text>
            {executionError.nodeId && (
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconTargetArrow size={14} />}
                onClick={handleJumpToFailedNode}
              >
                {jumpLabel}
              </Button>
            )}
          </Group>
        </Alert>
      )}

      <div className={classes.mainArea}>
      <div
        className={classes.flowContainer}
        onMouseMove={handleMouseMove}
        onContextMenu={handleContextMenu}
      >
        {isLoading ? (
          <div className={classes.loading}>
            <div className={classes.spinner} />
            <span>Loading flow...</span>
          </div>
        ) : (
          <>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={readOnly ? undefined : onNodesChange}
              onEdgesChange={readOnly ? undefined : onEdgesChange}
              onConnect={readOnly ? undefined : onConnect}
              onEdgesDelete={readOnly ? undefined : onEdgesDelete}
              onBeforeDelete={readOnly ? undefined : onBeforeDelete}
              onNodesDelete={readOnly ? undefined : onNodesDelete}
              onNodeDragStop={readOnly ? undefined : onNodeDragStop}
              onReconnect={readOnly ? undefined : onReconnect}
              onReconnectStart={readOnly ? undefined : onReconnectStart}
              onReconnectEnd={readOnly ? undefined : onReconnectEnd}
              onNodeDoubleClick={onNodeDoubleClick}
              defaultEdgeOptions={{ reconnectable: true }}
              reconnectRadius={20}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              connectionLineComponent={TypedConnectionLine}
              fitView
              fitViewOptions={{ padding: 0.7 }}
              zoomOnScroll
              zoomOnDoubleClick={false}
              panOnDrag={[1]}
              selectionOnDrag={false}
              multiSelectionKeyCode="Shift"
              deleteKeyCode={readOnly ? [] : [ 'Backspace', 'Delete' ]}
              nodesDraggable={!readOnly}
              nodesConnectable={!readOnly}
              elementsSelectable={!readOnly}
              className={classes.reactFlow}
            >
              <MiniMap
                pannable
                zoomable
                className={classes.minimap}
                nodeColor={minimapNodeColor}
              />
              <Controls
                showInteractive={!readOnly}
                className={classes.controls}
              />
              <Background
                gap={16}
                size={1}
                color={themeMode === 'dark' ? '#374151' : '#d1d5db'}
                className={classes.background}
              />
            </ReactFlow>
            <EdgeTooltip
              selectedEdges={selectedEdges}
              onToggleOptional={handleToggleOptional}
              readOnly={readOnly}
            />
          </>
        )}

        {!readOnly && (
          <NodePalette
            open={paletteOpen}
            registry={nodeRegistry}
            position={palettePosition}
            onSelectNode={handlePaletteSelect}
            onClose={closePalette}
          />
        )}
      </div>

      {executionTrace && (
        <NodeOutputsPanel
          nodeTraces={executionTrace.nodeTraces ?? {}}
          onNavigateNode={handleJumpToNode}
          highlightedNodeId={flashNodeId}
          traceLabels={traceLabels}
        />
      )}
      </div>

      {!readOnly && (
        <NodeConfigDrawer
          open={configDrawerOpen}
          onClose={() => setConfigDrawerOpen( false )}
          selectedNodeId={selectedNodeId}
          nodeConfig={selectedNodeId ? ( nodeConfigs[selectedNodeId] ?? {}) : {}}
          registryEntry={selectedNodeId ? ( nodeRegistry[( nodes.find(( n ) => n.id === selectedNodeId )?.data as RuleNodeData )?.type] ?? null ) : null}
          resolvedInputs={selectedNodeId && nodeRegistry[( nodes.find(( n ) => n.id === selectedNodeId )?.data as RuleNodeData )?.type]?.name === 'CodeBlock'
            ? resolveCodeBlockInputTypes( selectedNodeId, edges, nodes, nodeRegistry )
            : []}
          resolvedOutputs={selectedNodeId && nodeConfigs[selectedNodeId] && nodeRegistry[( nodes.find(( n ) => n.id === selectedNodeId )?.data as RuleNodeData )?.type]?.name === 'CodeBlock'
            ? resolveCodeBlockOutputTypes( nodeConfigs[selectedNodeId])
            : []}
          onValuesChange={handleValuesChange}
          onInputsConfigChange={handleInputsConfigChange}
          declaredInputs={declaredInputs}
          onDeclaredInputsChange={handleDeclaredInputsChange}
          theme={themeMode}
        />
      )}
    </div>
  );
}

export function RuleEditor( props: RuleEditorProps ) {
  return (
    <ReactFlowProvider>
      <RuleEditorInner {...props} />
    </ReactFlowProvider>
  );
}
